import { z } from "zod";
import { zodQueryStringArray } from "../utils/schema.js";
import {
  PaginationMetaResponseSchema,
  PaginationQuerySchema,
  SortingQuerySchema,
} from "./Pagination.js";
import { TagMinimalSchema, TagSchema } from "./Tag.js";

export const AssetVersionContentSchema = z.object({
  text: z.string(),
  createPrompt: z.string().optional(),
});

export const _AssetVersionSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  creatorId: z.uuid(),
  content: AssetVersionContentSchema,
  createdByAssistant: z.boolean().default(false),
  createdByApiKeyId: z.uuid().nullish(),
  createdByOAuthClientId: z.string().nullish(),
  createdAt: z.date(),
  title: z.string(),
  description: z.string().nullable(),
});

export const AssetConfigurationSchema = z.object({
  migratedFromConvoId: z.uuid().optional(),
});

/**
 * The asset shape the tRPC routes return: mostly DB columns, but with the
 * active version flattened in and tags reduced to what the UI renders.
 */
export const _AssetSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
  private: z.boolean(),
  configuration: AssetConfigurationSchema,
  createdByAssistant: z.boolean().default(false),
  tags: z.array(TagMinimalSchema.extend({ addedAt: z.date() })),
  activeAssetId: z.uuid(),
  assetVersion: _AssetVersionSchema.extend(
    z.object({
      isActive: z.boolean().default(false),
    }).shape,
  ),
});

export const _AssetWithCreatorSchema = _AssetSchema.extend({
  creator: z.object({
    id: z.uuid(),
    name: z.string(),
  }),
});

export const _CreateAssetVersionSchema = z.object({
  title: _AssetVersionSchema.shape.title,
  description: _AssetVersionSchema.shape.description,
  createdByAssistant: _AssetVersionSchema.shape.createdByAssistant.optional(),
  content: _AssetVersionSchema.shape.content,
  tagIds: z.array(TagSchema.shape.id).optional().default([]),
  assetId: z.uuid().optional(),
});

export type AssetConfiguration = z.infer<typeof AssetConfigurationSchema>;
export type Asset = z.infer<typeof _AssetSchema>;
export type AssetVersionContent = z.infer<typeof AssetVersionContentSchema>;
export type AssetVersion = z.infer<typeof _AssetVersionSchema>;
export type CreateAssetVersion = z.infer<typeof _CreateAssetVersionSchema>;

export const CreateAssetToolInputSchema = z.object({
  title: z
    .string()
    .min(1)
    .describe(
      'A short title for the asset, as descriptive as possible; e.g "Investor Email - Experiment Outcomes Summary"',
    ),
  description: z
    .string()
    .min(1)
    .describe(
      'A short description of the asset being created; e.g "Email to investors outlining recent experiments and outcomes"',
    ),
  content: z
    .string()
    .min(1)
    .describe(
      "The text content for this asset, e.g for an email template, ONLY the body of the template should be included.",
    ),
  prompt: z
    .string()
    .describe(
      "A prompt that can be used to recreate this asset in the future. Assume no knowledge of the asset's content, and include it here if necessary (e.g if asked to translate something, include the original text in this prompt)",
    )
    .optional(),
});
export const UpdateAssetToolInputSchema = z.object({
  assetId: z.uuid().describe("The asset to add a version to."),
  title: CreateAssetToolInputSchema.shape.title
    .optional()
    .describe("Kept from the current version when omitted."),
  description: CreateAssetToolInputSchema.shape.description
    .optional()
    .describe("Kept from the current version when omitted."),
  content: CreateAssetToolInputSchema.shape.content,
  prompt: CreateAssetToolInputSchema.shape.prompt,
});
export const AssetVersionToolOutputSchema = z.object({
  id: z.string().describe("The asset id."),
  versionId: z.string(),
  title: z.string(),
  tags: z.array(z.string()),
  createdAt: z.string(),
});
export type CreateAssetToolInput = z.infer<typeof CreateAssetToolInputSchema>;
export type UpdateAssetToolInput = z.infer<typeof UpdateAssetToolInputSchema>;
export type AssetVersionToolOutput = z.infer<
  typeof AssetVersionToolOutputSchema
>;

export function toAssetVersionToolOutput(
  version: { id: string; assetId: string; title: string; createdAt: Date },
  tags: { name: string }[],
): AssetVersionToolOutput {
  return {
    id: version.assetId,
    versionId: version.id,
    title: version.title,
    tags: tags.map(({ name }) => name),
    createdAt: version.createdAt.toISOString(),
  };
}

/**
 * ========================================
 * PUBLIC API SCHEMAS
 * ========================================
 */
export const GetAssetsQuerySchema = z.object({
  tag_id: zodQueryStringArray(z.uuid()),
  ...PaginationQuerySchema.shape,
  ...SortingQuerySchema.shape,
});

export const AssetTagItemSchema = z.object({
  id: TagSchema.shape.id,
  name: TagSchema.shape.name,
  addedAt: z.date(),
});

const AssetSchema = z.object({
  id: z.string(),
  originalId: _AssetSchema.shape.id,
  createdAt: _AssetSchema.shape.createdAt,
  updatedAt: _AssetSchema.shape.updatedAt.nullable(),
  tags: z.array(AssetTagItemSchema),
  activeVersion: z.object({
    id: _AssetVersionSchema.shape.id,
    title: _AssetVersionSchema.shape.title,
    description: _AssetVersionSchema.shape.description.nullable(),
    createPrompt: AssetVersionContentSchema.shape.createPrompt.nullable(),
    text: AssetVersionContentSchema.shape.text,
  }),
});

export const GetAssetsResponseSchema = z.object({
  ...PaginationMetaResponseSchema.shape,
  items: z.array(AssetSchema),
});

export type GetAssetsQuery = z.infer<typeof GetAssetsQuerySchema>;
export type GetAssetsResponse = z.infer<typeof GetAssetsResponseSchema>;

const ActiveVersionSchema = z.object({
  id: _AssetVersionSchema.shape.id,
  title: _AssetVersionSchema.shape.title,
  description: _AssetVersionSchema.shape.description.nullable(),
  createPrompt: AssetVersionContentSchema.shape.createPrompt.nullable(),
  text: AssetVersionContentSchema.shape.text,
  createdAt: _AssetVersionSchema.shape.createdAt,
});

export const PostAssetRequestSchema = z.object({
  title: _AssetVersionSchema.shape.title,
  description: _AssetVersionSchema.shape.description.optional(),
  text: AssetVersionContentSchema.shape.text,
  createPrompt: AssetVersionContentSchema.shape.createPrompt,
  tagIds: z.array(TagSchema.shape.id).optional().default([]),
});

export const PostAssetResponseSchema = z.object({
  asset: z.object({
    id: _AssetSchema.shape.id,
    createdAt: _AssetSchema.shape.createdAt,
    tags: z.array(AssetTagItemSchema),
    activeVersion: ActiveVersionSchema,
  }),
});

export type PostAssetRequest = z.infer<typeof PostAssetRequestSchema>;
export type PostAssetResponse = z.infer<typeof PostAssetResponseSchema>;

export const PostAssetVersionParamsSchema = z.object({
  assetId: z.uuid(),
});

export const PutAssetTagsParamsSchema = z.object({
  assetId: z.uuid(),
});

export const PutAssetTagsRequestSchema = z.object({
  tagIds: z.array(z.uuid()),
});

export const PostAssetVersionRequestSchema = z.object({
  title: _AssetVersionSchema.shape.title.optional(),
  description: _AssetVersionSchema.shape.description.optional(),
  text: AssetVersionContentSchema.shape.text,
  createPrompt: AssetVersionContentSchema.shape.createPrompt,
});

export const PostAssetVersionResponseSchema = z.object({
  asset: z.object({
    id: _AssetSchema.shape.id,
    activeVersion: ActiveVersionSchema,
  }),
});

export type PostAssetVersionParams = z.infer<
  typeof PostAssetVersionParamsSchema
>;
export type PostAssetVersionRequest = z.infer<
  typeof PostAssetVersionRequestSchema
>;
export type PutAssetTagsParams = z.infer<typeof PutAssetTagsParamsSchema>;
export type PutAssetTagsRequest = z.infer<typeof PutAssetTagsRequestSchema>;
export type PostAssetVersionResponse = z.infer<
  typeof PostAssetVersionResponseSchema
>;
/**
 * ========================================
 * END PUBLIC API SCHEMAS
 * ========================================
 */
